import { useEffect, useState } from "react";

/**
 * Sprint 16 Wave F — viewport-width hook for the mobile drawer
 * breakpoint.
 *
 * Returns `true` when the viewport is narrower than 768 px (the
 * Operator-chosen mobile breakpoint, matching Tailwind's `md`).
 * Updates reactively on resize via `MediaQueryList.change`.
 *
 * Accepts an optional `query` override for call sites with their own
 * breakpoint (e.g. the Trips Data tab's 700 px table→card switch,
 * A-TRIPS-DATA-003932 point 8) instead of the shared 767 px default.
 *
 * SSR-safe: returns `false` during the first render in environments
 * without `window` (jsdom in non-component-test mode, server
 * rendering if it ever happens). The first browser-side commit
 * resolves it correctly via the `useEffect`.
 */
export function useIsMobile(query: string = MOBILE_QUERY): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches); // resync after mount
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);

  return isMobile;
}

export const MOBILE_QUERY = "(max-width: 767px)";
